import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchMatchByIdAPI,
  updateMatchResultAPI,
  setNoPresentacionAPI,
  clearMatchScheduleAPI,
  fetchMatchCategoryAPI,
  deleteMatchResultsAPI,
} from '../../services/partidoService';

// AsyncThunk para obtener un partido por ID
export const fetchMatchById = createAsyncThunk(
  'partido/fetchById',
  async (matchId, { rejectWithValue }) => {
    try {
      const response = await fetchMatchByIdAPI(matchId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error al obtener el partido');
    }
  }
);

// Thunk asíncrono para limpiar fecha, hora y estadio de un partido
export const clearMatchSchedule = createAsyncThunk(
  'torneo/clearMatchSchedule',
  async (matchId, { rejectWithValue }) => {
    try {
      const response = await clearMatchScheduleAPI(matchId);
      // asumo que la API responde { message, partido: { ... } }
      return response.partido;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Error al limpiar la programación del partido'
      );
    }
  }
);

// AsyncThunk para borrar resultados
export const deleteMatchResults = createAsyncThunk(
  'partido/deleteResults',
  async (matchId, { rejectWithValue }) => {
    try {
      const response = await deleteMatchResultsAPI(matchId);
      return response; // { message, partido }
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error al borrar resultados');
    }
  }
);

// AsyncThunk para actualizar un partido
export const updateMatchResult = createAsyncThunk(
  'partido/updateResult',
  async (
    {
      matchId,
      resultadosSets,
      sets_local,
      sets_visitante,
      dia,
      hora,
      id_estadio,
      id_federacion,
    },
    { rejectWithValue }
  ) => {
    try {
      // Construir dinámicamente los campos necesarios
      const updateData = {};
      if (id_federacion) updateData.id_federacion = id_federacion;
      if (resultadosSets) updateData.resultadosSets = resultadosSets;
      if (sets_local !== undefined) updateData.sets_local = sets_local;
      if (sets_visitante !== undefined) updateData.sets_visitante = sets_visitante;
      if (dia) updateData.dia = dia;
      if (hora) updateData.hora = hora;
      if (id_estadio) updateData.id_estadio = id_estadio;
      
      const response = await updateMatchResultAPI(matchId, updateData);
      return response;
    } catch (error) {
      console.error('Error en AsyncThunk:', error);
      return rejectWithValue(error.response?.data || 'Error al actualizar el partido');
    }
  }
);

// AsyncThunk para actualizar partido a NO PRESENTACION
export const setNoPresentacion = createAsyncThunk(
  'partido/setNoPresentacion',
  async ({matchId, tipo}, { rejectWithValue }) => {
    try {
      const datosNP = {}
      datosNP.matchId = matchId
      datosNP.tipo = tipo
      const response = await setNoPresentacionAPI(datosNP);
      return response;
    } catch (error) {
      console.error('Error en setNoPresentacion AsyncThunk:', error);
      return rejectWithValue(error.response?.data || 'Error al actualizar partido a NO PRESENTACION');
    }
  }
);

export const fetchMatchCategory = createAsyncThunk(
  'partido/fetchCategory',
  async ({ torneoId, numeroPartido }, { rejectWithValue }) => {
    try {
      const data = await fetchMatchCategoryAPI(torneoId, numeroPartido);
      // data = { id_torneo_categoria: 123 }
      return data.id_torneo_categoria;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Error al obtener categoría del partido'
      );
    }
  }
);

const partidoSlice = createSlice({
  name: 'partido',
  initialState: {
    partidoData: null, // Información completa del partido
    resultadosSets: [], // Resultados de los sets
    loading: false, // Estado de carga
    error: null, // Mensajes de error
  },
  reducers: {
    clearPartidoData(state) {
      state.partidoData = null;
      state.resultadosSets = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Match By ID
      .addCase(fetchMatchById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMatchById.fulfilled, (state, action) => {
        state.loading = false;
        state.partidoData = action.payload;
        state.resultadosSets = action.payload.resultadosSets || []; // Carga los resultados de los sets
      })
      .addCase(fetchMatchById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error al obtener el partido';
      })
      // Update Match Result
      .addCase(updateMatchResult.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMatchResult.fulfilled, (state, action) => {
        state.loading = false;
        state.partidoData = {
          ...state.partidoData,
          sets_local: action.payload.sets_local,
          sets_visitante: action.payload.sets_visitante,
        };
        state.resultadosSets = action.payload.resultadosSets || [];
      })
      .addCase(updateMatchResult.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error al actualizar el partido';
      })
      // setNoPresentacion
      .addCase(setNoPresentacion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(setNoPresentacion.fulfilled, (state, action) => {
        state.loading = false;
        state.partidoData = action.payload.partido;
        state.resultadosSets = []; // Reiniciamos los resultados
      })
      .addCase(setNoPresentacion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error al actualizar partido a NO PRESENTACION';
      })
      .addCase(deleteMatchResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMatchResults.fulfilled, (state, action) => {
        state.loading = false;
        // Actualiza el partidoData con el que viene del servidor
        state.partidoData = action.payload.partido;
        state.resultadosSets = [];
      })
      .addCase(deleteMatchResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Error al borrar resultados';
      });
  },
});

export const { clearPartidoData } = partidoSlice.actions;
export default partidoSlice.reducer;
